// Log personal de rondas. Los tipos espejan @ygoargentina/helpers/player-log
// (el SDK no puede importar de helpers: la dependencia va al revés).
import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { Nullable, UserWithToken, WithData } from './api.types';

export type PlayerLogGameDto = { gameNumber: number; wentFirst: boolean; won: boolean | null };

export type PlayerLogRoundDto = {
  clientId: string;
  roundNumber: number;
  phase: 'SWISS' | 'TOP_CUT';
  isBye: boolean;
  opponentUserId: number | null;
  opponentName: string | null;
  oppMetaDeckId: number | null;
  oppDeckName: string | null;
  wonDiceRoll: boolean | null;
  resultType: 'WIN' | 'DRAW' | 'DOUBLE_LOSS' | null;
  notes: string | null;
  games: PlayerLogGameDto[];
};

export type PlayerLogRunDto = {
  clientId: string;
  name: string;
  eventType: 'LOCAL' | 'REGIONAL' | 'YCS' | 'ONLINE' | 'TESTING' | 'OTHER';
  playedAt: string;
  myMetaDeckId: number | null;
  myDeckName: string | null;
  notes: string | null;
  placement: number | null;
  playerCount: number | null;
  tournamentId: number | null;
  clientUpdatedAt: string;
  deletedAt: string | null;
  rounds: PlayerLogRoundDto[];
};

export type PlayerRunSyncResultDto = {
  clientId: string;
  /** REJECTED = inválido, no se reintenta. RETRY = falla transitoria del servidor. */
  status: 'OK' | 'CONFLICT_SERVER_WINS' | 'REJECTED' | 'RETRY';
  reason: string | null;
  run: PlayerLogRunDto | null;
};

export type PlayerLogRunsPageDto = { runs: PlayerLogRunDto[]; cursor: string };

export type PlayerLogStatsFilterDto = {
  myDeck?: string;
  eventTypes?: PlayerLogRunDto['eventType'][];
  from?: string;
  to?: string;
  runClientId?: string;
};

const RUN_FRAGMENT = gql`
  fragment PlayerLogRunFields on PlayerLogRun {
    clientId
    name
    eventType
    playedAt
    myMetaDeckId
    myDeckName
    notes
    placement
    playerCount
    tournamentId
    clientUpdatedAt
    deletedAt
    rounds {
      clientId
      roundNumber
      phase
      isBye
      opponentUserId
      opponentName
      oppMetaDeckId
      oppDeckName
      wonDiceRoll
      resultType
      notes
      games {
        gameNumber
        wentFirst
        won
      }
    }
  }
`;

export const syncPlayerRuns = async (
  user: Nullable<UserWithToken>,
  runs: PlayerLogRunDto[]
) =>
  getGraphQLQueryClient(user).request<WithData<PlayerRunSyncResultDto[]>>(
    gql`
      ${RUN_FRAGMENT}
      mutation syncPlayerRuns($runs: [PlayerLogRunInput!]!) {
        data: syncPlayerRuns(runs: $runs) {
          clientId
          status
          reason
          run {
            ...PlayerLogRunFields
          }
        }
      }
    `,
    { runs }
  );

export const getMyPlayerRuns = async (
  user: Nullable<UserWithToken>,
  updatedSince: string | null
) =>
  getGraphQLQueryClient(user).request<WithData<PlayerLogRunsPageDto>>(
    gql`
      ${RUN_FRAGMENT}
      query myPlayerRuns($updatedSince: String) {
        data: myPlayerRuns(updatedSince: $updatedSince) {
          cursor
          runs {
            ...PlayerLogRunFields
          }
        }
      }
    `,
    { updatedSince }
  );

export const getMyPlayerLogStats = async (
  user: Nullable<UserWithToken>,
  filter: PlayerLogStatsFilterDto = {}
) =>
  getGraphQLQueryClient(user).request<WithData<unknown>>(
    gql`
      query myPlayerLogStats($filter: PlayerLogStatsFilter) {
        data: myPlayerLogStats(filter: $filter)
      }
    `,
    { filter }
  );
